import { create } from 'zustand'
import { User } from '@/types/auth'
import * as authLib from '@/lib/auth'

interface AuthStore {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setLoading: (loading: boolean) => void
}

/**
 * Auth store using Zustand
 * Sessions are stored in Sanity.io (not localStorage per project rules)
 */
export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true })
      const response = await authLib.login({ email, password })
      
      // Session is now created in authLib.login() with unique session ID
      // No need to create it again here
      
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
      })
    } catch (error) {
      set({ isLoading: false })
      throw error
    }
  },

  logout: async () => {
    try {
      await authLib.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Always clear local state
      set({
        user: null,
        isAuthenticated: false,
      })
    }
  },

  checkAuth: async () => {
    try {
      set({ isLoading: true })
      const user = await authLib.getCurrentUser()
      
      if (user) {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
        })
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        })
      }
    } catch (error) {
      console.error('Auth check error:', error)
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      })
    }
  },
}))
